import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { AiChatService } from '@app/core/services/ai-chat.service';
import { LayoutService } from '@app/core/services/layout.service';
import { RememberService } from '@app/core/services/remember.service';
import { Subscription } from 'rxjs';
import { AiChatPanelService } from './ai-chat-panel.service';

@Component({
    selector: 'tt-ai-chat-panel',
    templateUrl: './ai-chat-panel.component.html',
    styleUrls: ['./ai-chat-panel.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AiChatPanelComponent implements OnDestroy, OnInit {
    private readonly maximumWidth = 720;

    private readonly closeThreshold = 240;

    private readonly panelOpenRememberId = 'w_ai_chat.panel_open';

    private readonly panelWidthRememberId = 'w_ai_chat.panel_width';

    private readonly tabletMediaQuery: MediaQueryList;

    private openSubscription?: Subscription;

    private applyingSettings = false;

    private resizing = false;

    private resizeStartX = 0;

    private resizeStartWidth = 0;

    /** Minimum desktop width retained after a resize gesture. */
    public readonly minimumWidth = 320;

    public width = 420;

    public open = false;

    public mobile = false;

    @HostBinding('class.tt-ai-chat-panel--open')
    public get openClass(): boolean {
        return this.open;
    }

    @HostBinding('class.tt-ai-chat-panel--mobile')
    public get mobileClass(): boolean {
        return this.mobile;
    }

    @HostBinding('style.width.px')
    public get hostWidth(): number {
        return this.open && !this.mobile ? this.width : 0;
    }

    public get currentMaximumWidth(): number {
        return Math.max(this.minimumWidth, Math.min(this.maximumWidth, window.innerWidth * 0.6));
    }

    constructor(
        public panel: AiChatPanelService,
        private aiChat: AiChatService,
        private cdr: ChangeDetectorRef,
        private layout: LayoutService,
        private remember: RememberService,
    ) {
        this.tabletMediaQuery = this.layout.getMediaQueries().tablet;
        this.setResponsiveMode();
    }

    /** Begins left-edge desktop resizing and captures the active pointer. */
    public onResizeStart(event: PointerEvent): void {
        if (this.mobile || event.button !== 0) {
            return;
        }

        event.preventDefault();
        this.resizing = true;
        this.resizeStartX = event.clientX;
        this.resizeStartWidth = this.width;
        (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    }

    public onResizeMove(event: PointerEvent): void {
        if (!this.resizing) {
            return;
        }

        event.preventDefault();
        this.setWidth(this.resizeStartWidth + this.resizeStartX - event.clientX, true);
    }

    public onResizeEnd(event: PointerEvent): void {
        if (!this.resizing) {
            return;
        }

        this.resizing = false;
        const target = event.currentTarget as HTMLElement;

        if (target.hasPointerCapture(event.pointerId)) {
            target.releasePointerCapture(event.pointerId);
        }

        this.restoreDocumentResizeStyles();

        if (this.width <= this.closeThreshold) {
            this.setWidth(this.resizeStartWidth);
            this.panel.close();
            return;
        }

        this.setWidth(this.width);
        this.persistWidth();
    }

    public onResizeKeydown(event: KeyboardEvent): void {
        if (this.mobile) {
            return;
        }

        const step = event.shiftKey ? 50 : 20;
        let newWidth: number | undefined;

        if (event.key === 'ArrowLeft') newWidth = this.width + step;
        if (event.key === 'ArrowRight') newWidth = this.width - step;
        if (event.key === 'Home') newWidth = this.minimumWidth;
        if (event.key === 'End') newWidth = this.currentMaximumWidth;

        if (newWidth === undefined) {
            return;
        }

        event.preventDefault();
        this.setWidth(newWidth);
        this.persistWidth();
    }

    ngOnInit(): void {
        let initialized = false;
        this.openSubscription = this.panel.open$.subscribe((open) => {
            this.open = open;

            if (initialized && this.panel.canUseAiChat && !this.applyingSettings) {
                void this.remember.remember(this.panelOpenRememberId, open ? 1 : 0).catch(() => undefined);
            }
            initialized = true;
            this.cdr.markForCheck();
        });
        this.tabletMediaQuery.addEventListener('change', this.setResponsiveMode);
        window.addEventListener('resize', this.onWindowResize);
        void this.loadPanel();
    }

    ngOnDestroy(): void {
        this.openSubscription?.unsubscribe();
        this.tabletMediaQuery.removeEventListener('change', this.setResponsiveMode);
        window.removeEventListener('resize', this.onWindowResize);
        this.restoreDocumentResizeStyles();
    }

    private readonly setResponsiveMode = (): void => {
        this.mobile = !this.tabletMediaQuery.matches;
        this.setWidth(this.width);
        this.cdr.markForCheck();
    };

    private readonly onWindowResize = (): void => this.setWidth(this.width);

    private setWidth(width: number, allowBelowMinimum = false): void {
        const minimumWidth = allowBelowMinimum ? 0 : this.minimumWidth;
        this.width = Math.round(Math.min(this.currentMaximumWidth, Math.max(minimumWidth, width)));
        this.cdr.markForCheck();
    }

    private persistWidth(): void {
        void this.remember.remember(this.panelWidthRememberId, this.width).catch(() => undefined);
    }

    private restoreDocumentResizeStyles(): void {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
    }

    /** Loads access, remembered width, and open state before allowing panel persistence. */
    private async loadPanel(): Promise<void> {
        this.applyingSettings = true;

        try {
            const settings = await this.aiChat.getAiChatPanel();
            this.panel.setSettings(settings);

            const width = Number(settings.panel_width);
            if (Number.isFinite(width) && width > 0) {
                this.setWidth(width);
            }

            if (settings.can_use_ai_chat === '1' && settings.panel_open === '1') {
                this.panel.open();
            } else {
                this.panel.close();
            }
        } catch {
            this.panel.close();
        } finally {
            this.applyingSettings = false;
            this.cdr.markForCheck();
        }
    }

}
